import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  Badge,
  Box,
  Tooltip,
  Switch,
  Skeleton,
} from "@mui/material";
import { Play, Square, FileText, X, BarChart2, Eye, ArrowUp, ArrowDown, GripVertical, RefreshCw } from 'lucide-react';
import { useNavigate } from "react-router-dom"
import api from '../utils/api';
import CONFIG from '../utils/config';

// Fonction utilitaire pour formater la date
const formatDate = (dateString) => {
  if (!dateString || dateString === "Jamais") return dateString;
  
  try {
    // Accepter toutes les dates, y compris celles dans le futur
    const date = new Date(dateString);
    
    // Vérifier si la date est valide
    if (isNaN(date.getTime())) {
      console.warn("Date invalide reçue:", dateString);
      // Essayer de parser manuellement le format ISO avec millisecondes
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        // Format ISO détecté, extraction manuelle des parties de la date
        const parts = dateString.split('T');
        const dateParts = parts[0].split('-');
        const timeParts = parts[1].split(':');
        
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1; // Les mois commencent à 0 en JS
        const day = parseInt(dateParts[2]);
        const hour = parseInt(timeParts[0]);
        const minute = parseInt(timeParts[1]);
        const second = parseInt(timeParts[2]);
        
        const manualDate = new Date(year, month, day, hour, minute, second);
        
        return manualDate.toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).replace(',', '');
      }
      
      return "Date invalide";
    }
    
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(',', '');
  } catch (error) {
    console.error("Erreur lors du formatage de la date:", error, dateString);
    return "Erreur de date";
  }
};

// Liste des marketplaces disponibles
const MARKETPLACES = [
  { id: "DE", name: "Allemagne" },
  { id: "IT", name: "Italie" },
  { id: "FR", name: "France" },
  { id: "ES", name: "Espagne" }
];

function Scraping() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningScripts, setRunningScripts] = useState({});
  const [progress, setProgress] = useState({});
  const [openLogs, setOpenLogs] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState("");
  const [selectedScript, setSelectedScript] = useState("");
  const [analysedFiles, setAnalysedFiles] = useState([]);
  const [exportFiles, setExportFiles] = useState([]);
  const navigate = useNavigate();
  
  // États pour la pop-up de sélection des marketplaces
  const [openMarketplaceDialog, setOpenMarketplaceDialog] = useState(false);
  const [selectedMarketplaces, setSelectedMarketplaces] = useState([]);
  
  // États pour le tri, le survol et la sélection des lignes
  const [sorting, setSorting] = useState([{ id: "scriptName", desc: false }]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('scrapingColumnOrder');
    return savedOrder ? JSON.parse(savedOrder) : [];
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const savedSizing = localStorage.getItem('scrapingColumnSizing');
    if (savedSizing) {
      return JSON.parse(savedSizing);
    }
    // Valeurs par défaut
    return {
      scriptName: 200,
      progress: 200,
      lastCompleted: 150,
      actions: 200,
    };
  });
  const [logLoading, setLogLoading] = useState(false);
  const [logContent, setLogContent] = useState("");

  useEffect(() => {
    // Fonction pour charger toutes les données initiales
    const loadInitialData = async () => {
      setLoading(true);
      try {
        // Exécuter tous les appels en parallèle
        const [scriptsRes, progressRes, filesRes, exportRes] = await Promise.all([
          api.get("/scraping/scripts"),
          api.get("/scraping/progress"),
          api.get("/api/analysed-files"),
          api.get("/scraping/export-files"),
        ]);

        // Mettre à jour les états
        setScripts(scriptsRes.data);
        
        // Traiter les données de progression
        const progressData = progressRes.data;
        setProgress(progressData);
        const running = {};
        Object.keys(progressData).forEach((scriptName) => {
          if (progressData[scriptName].status === "En cours..." || 
              progressData[scriptName].status === "running") {
            running[scriptName] = true;
          }
        });
        setRunningScripts(running);

        // Traiter les fichiers analysés
        const filesData = Array.isArray(filesRes.data.data) ? filesRes.data.data : [];
        setAnalysedFiles(filesData);

        // Traiter les fichiers d'export
        if (exportRes.data) {
          setExportFiles(exportRes.data);
        }

      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    // Charger les données initiales
    loadInitialData();

    // Configurer l'intervalle pour la mise à jour de la progression
    const interval = setInterval(() => {
      fetchProgress();
    }, 10000);

    return () => clearInterval(interval);
  }, []); // Dépendances vides car c'est un chargement initial

  // Sauvegarder la configuration des colonnes
  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem('scrapingColumnOrder', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);

  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      localStorage.setItem('scrapingColumnSizing', JSON.stringify(columnSizing));
    }
  }, [columnSizing]);

  const fetchProgress = async () => {
    try {
      const res = await api.get("/scraping/progress");
      setProgress(res.data);
      const running = {};
      Object.keys(res.data).forEach((scriptName) => {
        if (res.data[scriptName].status === "En cours..." || 
            res.data[scriptName].status === "running") {
          running[scriptName] = true;
        }
      });
      setRunningScripts(running);
    } catch (err) {
      console.error("Erreur lors de la récupération de la progression:", err);
    }
  };


  const handleOpenLogs = (scriptName) => {
    setSelectedScript(scriptName);
    // Ouvrir le dialogue et charger les logs
    setOpenLogs(true);
    fetchLog(scriptName);
  };

  const handleCloseLogs = () => {
    setOpenLogs(false);
    setSelectedLogs("");
  };

  const handleOpenMarketplaceDialog = (script) => {
    setSelectedScript(script);
    setOpenMarketplaceDialog(true);
  };

  const handleCloseMarketplaceDialog = () => {
    setOpenMarketplaceDialog(false);
    setSelectedMarketplaces([]);
  };

  const handleMarketplaceChange = (event) => {
    const value = event.target.value;
    setSelectedMarketplaces(typeof value === 'string' ? value.split(',') : value);
  };

  const handleAnalyze = () => {
    // Extraire le nom de base du script (sans .py)
    const baseScriptName = selectedScript.replace('.py', '');
    const fileName = `${baseScriptName}_products.csv`;
    
    // On peut récupérer l'ID du script à partir de la progression
    const scriptProgress = progress[selectedScript] || {};
    const scriptId = scriptProgress.scriptId || null;
    
    // S'assurer que le nom du script a l'extension .py
    const scriptName = selectedScript.endsWith('.py') ? selectedScript : `${selectedScript}.py`;
    
    // Utiliser le nom du fichier complet avec l'extension .csv pour la notification
    const cleanFileName = fileName;
    
    api.post("/api/analyze/file", {
      fileName,
      scriptId,
      marketplaceFilter: selectedMarketplaces.length > 0 ? selectedMarketplaces : undefined,
      sourceType: 'extract',
      scriptName: scriptName, // Le nom du script original avec .py
      baseScriptName: cleanFileName // Le nom complet avec .csv pour les notifications
    })
    .then(() => {
      handleCloseMarketplaceDialog();
      fetchProgress();
    })
    .catch((error) => {
      console.error('Erreur lors de l\'analyse:', error);
    });
  };

  const handleRunScript = (scriptName) => {
    // Extraire le nom du script sans l'extension .py
    const baseScriptName = scriptName.endsWith('.py') ? scriptName.slice(0, -3) : scriptName;
    
    // Variables pour stocker l'ID du script
    let scriptId = null;
    
    
    // Mettre à jour le statut dans la BDD pour indiquer que le script est en cours d'exécution
    api.post("/api/update_progress", {
      script_name: scriptName,
      data: {
        status: "En cours...",
        progress: "0/0 pages",
        lastCompleted: null
      },
      table_name: "Scripts"
    })
    .then(() => {
      // Appeler le backend qui se chargera d'appeler l'API Flask sur le VPS
      return api.post("/scraping/run", {
        scriptName: baseScriptName,
        useVpn: false
      });
    })
    .then(response => {
      console.log(`Script ${scriptName} lancé:`, response.data);
      
      // Stocker l'ID du script si disponible
      if (response.data && response.data.scriptId) {
        scriptId = response.data.scriptId;
        console.log(`Script ID: ${scriptId}`);
      }
      
      // Mettre à jour la progression
      return api.post("/api/update_progress", {
        script_name: scriptName,
        data: {
          status: "En cours...",
          progress: "Script démarré sur le VPS"
        },
        table_name: "Scripts"
      });
    })
    .then(fetchProgress)
    .catch((err) => {
      console.error("Erreur lors du lancement du script:", err);
      // Mettre à jour le statut pour indiquer l'erreur
      api.post("/api/update_progress", {
        script_name: scriptName,
        data: {
          status: "Erreur",
          progress: "Échec du lancement sur le VPS",
          lastCompleted: new Date().toISOString()
        },
        table_name: "Scripts"
      }).then(fetchProgress);
      
      alert("Erreur lors du lancement du script sur le VPS");
    });
  };

  const handleViewFile = (scriptName) => {
    // Utiliser l'extension .csv.json sans doubler l'extension .json
    const fileName = scriptName.replace('.py', '_products.csv');
    navigate('/analyse', { state: { sourceFile: fileName } });
  };

  const isFileNew = (scriptName) => {
    // Extraire le nom de base du script sans l'extension .py
    const baseScriptName = scriptName.replace('.py', '');
    const fileName = `${baseScriptName}_products.csv.json`;
    // Rechercher le fichier dans la liste des fichiers analysés
    const file = Array.isArray(analysedFiles) ? analysedFiles.find(f => f.name === fileName) : null;
    return file && file.isNew;
  };

  const fileExists = (scriptName) => {
    // Extraire le nom de base du script sans l'extension .py
    const baseScriptName = scriptName.replace('.py', '');
    const fileName = `${baseScriptName}_products.csv.json`;
    // Vérifier si un fichier analysé existe pour ce script
    return Array.isArray(analysedFiles) ? analysedFiles.some(f => f.name === fileName) : false;
  };

  const hasExportFile = (scriptName) => {
    // Extraire le nom de base du script sans l'extension .py
    const baseScriptName = scriptName.replace('.py', '');
    const fileName = `${baseScriptName}_products.csv`;
    // Vérifier si un fichier d'export existe pour ce script
    return exportFiles.some(file => file === fileName);
  };

  // Fonction pour trier les scripts
  const getSortedScripts = () => {
    if (!scripts.length) return [];
    
    return [...scripts].sort((a, b) => {
      if (sorting[0].id === "scriptName") {
        // Tri par nom de script
        return sorting[0].desc ? b.localeCompare(a) : a.localeCompare(b);
      }
      
      // Par défaut, tri par nom de script
      return a.localeCompare(b);
    });
  };

  // Fonction pour gérer le changement de tri
  const handleSortChange = (columnId) => {
    setSorting(prev => {
      const currentSort = prev.find(sort => sort.id === columnId);
      if (currentSort) {
        // Si la colonne est déjà triée, inverser la direction
        return prev.map(sort => 
          sort.id === columnId 
            ? { ...sort, desc: !sort.desc } 
            : sort
        );
      } else {
        // Sinon, ajouter un nouveau tri
        return [{ id: columnId, desc: true }];
      }
    });
  };

  // Fonction pour gérer le survol des lignes
  const handleRowHover = (scriptName) => {
    setHoveredRowId(scriptName);
  };

  // Fonction pour gérer la sélection des lignes
  const handleRowClick = (scriptName) => {
    setSelectedRowId(selectedRowId === scriptName ? null : scriptName);
  };

  // Fonction pour gérer le redimensionnement des colonnes
  const handleColumnResize = (columnId, newSize) => {
    setColumnSizing(prev => ({
      ...prev,
      [columnId]: newSize
    }));
  };

  // Fonction pour gérer le réordonnancement des colonnes
  const handleColumnReorder = (sourceId, targetId) => {
    if (sourceId === targetId) return;
    
    setColumnOrder(prev => {
      const newOrder = [...prev];
      const sourceIndex = newOrder.indexOf(sourceId);
      const targetIndex = newOrder.indexOf(targetId);
      
      // Supprimer la colonne source
      newOrder.splice(sourceIndex, 1);
      // Insérer la colonne source à la position cible
      newOrder.splice(targetIndex, 0, sourceId);
      
      return newOrder;
    });
  };

  const fetchLog = async (scriptName) => {
    // Déplacer la déclaration en dehors du bloc try
    const baseScriptName = scriptName.endsWith('.py') ? scriptName.slice(0, -3) : scriptName;
    
    try {
      setLogLoading(true);
      
      console.log(`Tentative de récupération des logs pour le script: ${baseScriptName}`);
      
      // Récupérer les logs depuis le backend
      const response = await api.get(`/scraping/logs`, {
        params: {
          scriptName: baseScriptName
        }
      });
      
      console.log('Réponse du serveur:', response);
      
      // Vérifier si la réponse contient un log sous forme d'objet ou de chaîne
      let logContent = "";
      if (response.data) {
        if (response.data.log) {
          logContent = response.data.log;
        } else if (typeof response.data === 'string') {
          logContent = response.data;
        } else {
          logContent = JSON.stringify(response.data, null, 2);
        }
      }
      
      if (!logContent || logContent.trim() === "") {
        // Si aucun log n'est disponible, ajouter un message informatif
        const scriptStatus = progress[scriptName] ? progress[scriptName].status : "inconnu";
        logContent = `Aucun log disponible pour le script ${scriptName}.\n\n` +
                     `Statut actuel: ${scriptStatus}\n` +
                     `Le script a peut-être été lancé récemment et n'a pas encore généré de logs.\n` +
                     `Vous pouvez cliquer sur "Rafraîchir" pour vérifier à nouveau.\n\n` +
                     `Détails techniques:\n` +
                     `- Nom du script: ${baseScriptName}\n` +
                     `- URL de la requête: ${api.defaults.baseURL}/scraping/logs?scriptName=${baseScriptName}\n` +
                     `- Réponse du serveur: ${JSON.stringify(response.data, null, 2)}`;
      }
      
      // Stocker pour affichage dans le dialogue
      setLogContent(logContent);
    } catch (error) {
      console.error("Erreur lors du chargement du log:", error);
      
      // Message d'erreur plus informatif
      const errorMessage = `Erreur lors du chargement du log: ${error.message}\n\n` +
                         `Détails techniques:\n` +
                         `- URL de la requête: ${api.defaults.baseURL}/scraping/logs?scriptName=${baseScriptName}\n` +
                         `- Code d'erreur: ${error.response?.status || 'N/A'}\n` +
                         `- Message d'erreur: ${error.response?.data?.message || error.message}\n\n` +
                         `Vérifiez que:\n` +
                         `1. Le serveur VPS est en ligne\n` +
                         `2. Le script s'est bien lancé\n` +
                         `3. Le fichier de log existe\n` +
                         `4. Les permissions du fichier de log sont correctes\n` +
                         `5. Le chemin du fichier de log est correct`;
      
      setLogContent(errorMessage);
    } finally {
      setLogLoading(false);
    }
  };

  // Fonction pour arrêter un script en cours d'exécution
  const handleStopScript = (scriptName, scriptId = null) => {
    // S'assurer que le nom du script a l'extension .py
    const scriptNameWithExt = scriptName.endsWith('.py') ? scriptName : `${scriptName}.py`;
    
    // Mettre à jour le statut dans la BDD
    api.post("/api/update_progress", {
      script_name: scriptNameWithExt,
      data: {
        status: "Arrêté",
        lastCompleted: new Date().toISOString()
      },
      table_name: "Scripts",
      force_update: true
    })
    .then(() => {
      // Appeler le backend avec le nom du script avec extension
      return api.post("/scraping/stop", {
        scriptName: scriptNameWithExt,
        scriptId: scriptId
      });
    })
    .then(response => {
      console.log(`Script ${scriptNameWithExt} arrêté:`, response.data);
      fetchProgress();
    })
    .catch(err => {
      console.error("Erreur lors de l'arrêt du script:", err);
      // Même en cas d'erreur, on met à jour le statut
      fetchProgress();
    });
  };

  // Fonction qui met à jour les dates de progress avec celles des fichiers analysés
  const updateDatesFromFiles = () => {
    if (!Array.isArray(analysedFiles) || !analysedFiles.length || !Object.keys(progress).length) return;

    const updatedProgress = { ...progress };
    let progressUpdated = false;

    scripts.forEach(scriptName => {
      const baseScriptName = scriptName.replace('.py', '');
      const fileName = `${baseScriptName}_products.csv.json`;
      const file = analysedFiles.find(f => f.name === fileName);

      // Mettre à jour si on trouve un fichier correspondant et que sa date est plus récente
      // ou si la date actuelle est invalide
      if (file && file.updated_at) {
        const currentDate = updatedProgress[scriptName]?.lastCompleted;
        const fileDate = file.updated_at;
        
        // Vérifier si la date actuelle est invalide ou si la date du fichier est plus récente
        if (!currentDate || 
            formatDate(currentDate) === "Date invalide" || 
            formatDate(currentDate) === "Erreur de date" ||
            new Date(fileDate) > new Date(currentDate)) {
          
          if (!updatedProgress[scriptName]) {
            updatedProgress[scriptName] = {};
          }
          
          updatedProgress[scriptName].lastCompleted = fileDate;
          progressUpdated = true;
        }
      }
    });

    // Mettre à jour l'état si des changements ont été effectués
    if (progressUpdated) {
      console.log("Mise à jour des dates de dernière analyse");
      setProgress(updatedProgress);
    }
  };

  // Ajouter une ref pour synchroniser les dates une seule fois
  const hasSyncedDatesRef = useRef(false);
  // Appeler la fonction de synchronisation des dates une seule fois après chargement initial
  useEffect(() => {
    if (!hasSyncedDatesRef.current && analysedFiles.length > 0 && Object.keys(progress).length > 0) {
      updateDatesFromFiles();
      hasSyncedDatesRef.current = true;
    }
  }, [analysedFiles, progress]);

  const TableSkeleton = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><Skeleton animation="wave" /></TableCell>
            <TableCell><Skeleton animation="wave" /></TableCell>
            <TableCell><Skeleton animation="wave" /></TableCell>
            <TableCell><Skeleton animation="wave" /></TableCell>
            <TableCell><Skeleton animation="wave" /></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[1, 2, 3, 4, 5].map((row) => (
            <TableRow key={row}>
              <TableCell><Skeleton animation="wave" /></TableCell>
              <TableCell>
                <Skeleton animation="wave" />
                <Skeleton animation="wave" height={10} sx={{ mt: 1 }} />
              </TableCell>
              <TableCell><Skeleton animation="wave" /></TableCell>
              <TableCell><Skeleton animation="wave" /></TableCell>
              <TableCell>
                <Box sx={{ display: "flex", gap: "8px" }}>
                  <Skeleton animation="wave" width={80} height={36} />
                  <Skeleton animation="wave" width={36} height={36} />
                  <Skeleton animation="wave" width={36} height={36} />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div style={{ padding: "20px", margin: "0 auto" }}>
      <Typography variant="h4" gutterBottom>
        Gestion des Scripts de Scraping 🚀
      </Typography>

      {loading ? (
        <TableSkeleton />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  style={{
                    width: columnSizing.scriptName,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => handleSortChange("scriptName")}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GripVertical 
                      sx={{ 
                        mr: 0.5, 
                        opacity: 0.2,
                        fontSize: '1rem',
                        '&:hover': { opacity: 0.5 }
                      }}
                    />
                    <strong>Nom du Script</strong>
                    {sorting.find(s => s.id === "scriptName") && (
                      sorting.find(s => s.id === "scriptName").desc ? 
                        <ArrowDown size={16} /> : 
                        <ArrowUp size={16} />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  style={{
                    width: columnSizing.progress,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => handleSortChange("progress")}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GripVertical 
                      sx={{ 
                        mr: 0.5, 
                        opacity: 0.2,
                        fontSize: '1rem',
                        '&:hover': { opacity: 0.5 }
                      }}
                    />
                    <strong>Progression</strong>
                    {sorting.find(s => s.id === "progress") && (
                      sorting.find(s => s.id === "progress").desc ? 
                        <ArrowDown size={16} /> : 
                        <ArrowUp size={16} />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  style={{
                    width: columnSizing.lastCompleted,
                    userSelect: "none",
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GripVertical 
                      sx={{ 
                        mr: 0.5, 
                        opacity: 0.2,
                        fontSize: '1rem',
                        '&:hover': { opacity: 0.5 }
                      }}
                    />
                    <strong>Dernier Scan</strong>
                  </Box>
                </TableCell>
                <TableCell
                  style={{
                    width: columnSizing.actions,
                    userSelect: "none",
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <GripVertical 
                      sx={{ 
                        mr: 0.5, 
                        opacity: 0.2,
                        fontSize: '1rem',
                        '&:hover': { opacity: 0.5 }
                      }}
                    />
                    <strong>Actions</strong>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getSortedScripts().map((script) => {
                const scriptProgress = progress[script] || {};
                const percentage = scriptProgress.progress
                  ? (parseInt(scriptProgress.progress.split("/")[0]) /
                      parseInt(scriptProgress.progress.split("/")[1])) *
                    100
                  : 0;
                
                const status = scriptProgress.status || "-";
                const progressText = scriptProgress.progress || "-";
                
                const isScrapingRunning = scriptProgress.status === "running" || scriptProgress.status === "En cours...";
                const hasNewFile = isFileNew(script);

                return (
                  <TableRow 
                    key={script}
                    onClick={(e) => {
                      // Ne pas sélectionner la ligne si on clique sur un élément interactif
                      if (e.target.closest('button, a, [role="button"], .MuiIconButton-root, .MuiSwitch-root')) {
                        return;
                      }
                      handleRowClick(script);
                    }}
                    onMouseEnter={() => handleRowHover(script)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    style={{
                      backgroundColor: script === selectedRowId 
                        ? 'rgba(33, 150, 243, 0.15)' 
                        : script === hoveredRowId 
                          ? 'rgba(0, 0, 0, 0.04)' 
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <TableCell>{script}</TableCell>
                    <TableCell>
                      {runningScripts[script] ? (
                        <>
                          <Typography variant="body2">
                            {isScrapingRunning ? 
                              progressText : "-"}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={
                              percentage}
                            style={{
                              height: "10px",
                              marginTop: "5px",
                              borderRadius: "5px",
                            }}
                          />
                        </>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // Essayer d'abord d'utiliser la date du script si elle existe et est valide
                        if (scriptProgress.lastCompleted) {
                          const formattedDate = formatDate(scriptProgress.lastCompleted);
                          if (formattedDate !== "Date invalide" && formattedDate !== "Erreur de date") {
                            return formattedDate;
                          }
                        }
                        
                        // Sinon chercher le fichier correspondant
                        const baseScriptName = script.replace('.py', '');
                        const fileName = `${baseScriptName}_products.csv.json`;
                        const file = analysedFiles.find(f => f.name === fileName);
                        
                        if (file && file.updated_at) {
                          return formatDate(file.updated_at);
                        }
                        
                        // Si aucune date valide n'est trouvée
                        return "-";
                      })()}
                    </TableCell>
                    <TableCell>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {!runningScripts[script] ? (
                          <>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<Play size={20} />}
                              onClick={() => handleRunScript(script)}
                            >
                              Lancer
                            </Button>
                            <Button
                              variant="contained"
                              color="secondary"
                              startIcon={<BarChart2 size={20} />}
                              onClick={() => handleOpenMarketplaceDialog(script)}
                              disabled={!hasExportFile(script)}
                              title={!hasExportFile(script) ? "Aucun fichier d'export disponible" : "Analyser le fichier"}
                            >
                              Analyser
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="contained"
                            color="error"
                            startIcon={<Square size={20} />}
                            onClick={() => handleStopScript(script, scriptProgress.scriptId)}
                          >
                            Arrêter
                          </Button>
                        )}
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenLogs(script)}
                        >
                          <FileText size={20} />
                        </IconButton>
                        {fileExists(script) && (
                          <Badge color="error" variant="dot" invisible={!isFileNew(script)}>
                            <IconButton
                              color="primary"
                              onClick={() => handleViewFile(script)}
                              title="Voir le fichier analysé"
                            >
                              <Eye size={20} />
                            </IconButton>
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openLogs}
        onClose={handleCloseLogs}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Logs pour {selectedScript}
          <IconButton
            onClick={handleCloseLogs}
            color="inherit"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <X size={20} />
          </IconButton>
          <IconButton
            onClick={() => fetchLog(selectedScript)}
            color="primary"
            sx={{ position: 'absolute', right: 48, top: 8 }}
            title="Rafraîchir les logs"
          >
            <RefreshCw size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {logLoading ? (
            <CircularProgress />
          ) : (
            <>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Dernière mise à jour: {new Date().toLocaleTimeString()}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fetchLog(selectedScript)}
                  startIcon={<RefreshCw size={20} />}
                >
                  Rafraîchir
                </Button>
              </Box>
              <Box 
                component="pre" 
                sx={{ 
                  whiteSpace: "pre-wrap", 
                  backgroundColor: "#f5f5f5", 
                  padding: 2,
                  borderRadius: 1,
                  maxHeight: "500px",
                  overflow: "auto",
                  fontFamily: "monospace"
                }}
              >
                {logContent}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openMarketplaceDialog} onClose={handleCloseMarketplaceDialog}>
        <DialogTitle>
          Sélectionner les marketplaces
          <IconButton
            aria-label="close"
            onClick={handleCloseMarketplaceDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <FormControl sx={{ m: 1, width: 300 }}>
            <InputLabel>Marketplaces</InputLabel>
            <Select
              multiple
              value={selectedMarketplaces}
              onChange={handleMarketplaceChange}
              input={<OutlinedInput label="Marketplaces" />}
              renderValue={(selected) => selected.join(', ')}
            >
              <MenuItem value="FR">France</MenuItem>
              <MenuItem value="DE">Allemagne</MenuItem>
              <MenuItem value="IT">Italie</MenuItem>
              <MenuItem value="ES">Espagne</MenuItem>
              <MenuItem value="UK">Royaume-Uni</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMarketplaceDialog}>Annuler</Button>
          <Button onClick={handleAnalyze} variant="contained" color="primary">
            Analyser
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Scraping;
